import { useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import { isArchived, RequestWithRelations } from '@/models/request';

import { ArticleCard } from './components/ArticleCard';
import { Choice, Choices } from './components/ArticleNameWithChoice';
import { ClientRequestPrice } from './components/ClientRequestPrice';
import { ClientShippingOption } from './components/ClientShippingOption';
import { DepositMessage } from './components/DepositMessage';
import { RefusedArticlesToPickup } from './components/RefusedArticlesToPickup';
import { getArticlesToDisplay } from './utils';

export const ClientArticlesList = ({
  request,
  hideStepper = false,
  showPrice = false,
  showArticleComment = false,
  choices,
  setChoice,
  showChoicesError,
  showShippingOptions,
  acceptedArticlesOnly = false,
}: {
  request: RequestWithRelations;
  hideStepper?: boolean;
  showPrice?: boolean;
  showArticleComment?: boolean;
  choices?: Choices;
  setChoice?: (articleId: string, choice: Choice) => void;
  showChoicesError?: boolean;
  showShippingOptions?: boolean;
  acceptedArticlesOnly?: boolean;
}) => {
  const { t } = useLingui();

  const { articlesToDisplay, articlesToPickup } = getArticlesToDisplay(request);

  const filteredArticlesToDisplay = acceptedArticlesOnly
    ? articlesToDisplay.filter((article) => !!article.quoteAcceptedAt)
    : articlesToDisplay;

  const showDepositedOn = !!request.store && !!request.client;
  const showStepper =
    filteredArticlesToDisplay.length + articlesToPickup.length > 1 && !hideStepper;

  return (
    <Stack gap="1rem">
      {!isArchived(request) && articlesToPickup.length > 0 && (
        <RefusedArticlesToPickup request={request} articles={articlesToPickup} />
      )}
      <Box padding="0" ariaLabel={t({ id: 'client.articles.table.label', message: 'Items' })}>
        {filteredArticlesToDisplay.map((article) => (
          <ArticleCard
            key={article.id}
            article={article}
            showStepper={showStepper}
            showPrice={showPrice}
            showArticleComment={showArticleComment}
            showChoices={!!choices}
            showArticleServices
            showDefectsNotToBeRepaired
            choice={choices?.[article.id]}
            setChoice={(choice) => setChoice?.(article.id, choice)}
            showChoicesError={showChoicesError}
          />
        ))}
      </Box>
      {showDepositedOn && <DepositMessage request={request} />}
      {showShippingOptions && <ClientShippingOption request={request} />}
      {showPrice && (
        <ClientRequestPrice price={request.organizationPrice?.amountPerCurrency?.[0]} />
      )}
    </Stack>
  );
};
